/**
 * CheckInEntryCard Component
 * Renders a single entry (menu item + rating + photos + review) inside a bordered card.
 * Used by QuickCheckInPage when building multi-entry check-ins.
 */

import Ionicons from '@expo/vector-icons/Ionicons';
import React, { memo, useCallback, useState } from 'react';
import { Pressable, View } from 'react-native';

import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { FriendPickerSheet } from '@/features/friend-recommendations/components/friend-picker-sheet';
import { MenuItem } from '@/types/menu-item';

import { CheckInEntryState, PhotoAsset, RecommendFriend } from '../types';
import { CompletenessHint } from './completeness-hint';
import { MenuItemSearchSection } from './menu-item-search-section';
import { PhotoSection } from './photo-section';
import { RatingButtonGroup } from './rating-button-group';
import { ReviewSection } from './review-section';

export interface CheckInEntryCardProps {
  entry: CheckInEntryState;
  entryIndex: number;
  businessId: string;
  businessName: string;
  isSubmitting: boolean;
  canRemove: boolean;
  onUpdate: (id: string, patch: Partial<CheckInEntryState>) => void;
  onRemove: (id: string) => void;
}

function CheckInEntryCardComponent({
  entry,
  entryIndex,
  businessId,
  businessName,
  isSubmitting,
  canRemove,
  onUpdate,
  onRemove,
}: CheckInEntryCardProps) {
  const handleSelectMenuItem = useCallback(
    (menuItem: MenuItem) => {
      onUpdate(entry.id, { menuItem });
    },
    [entry.id, onUpdate]
  );

  const handleClearMenuItem = useCallback(() => {
    onUpdate(entry.id, { menuItem: null });
  }, [entry.id, onUpdate]);

  const handleSetRating = useCallback(
    (ratingId: number, ratingKey: string) => {
      onUpdate(entry.id, { ratingId, ratingKey });
    },
    [entry.id, onUpdate]
  );

  const handleAddPhoto = useCallback(
    (photo: PhotoAsset) => {
      onUpdate(entry.id, { photos: [...entry.photos, photo] });
    },
    [entry.id, entry.photos, onUpdate]
  );

  const handleRemovePhoto = useCallback(
    (photoId: string) => {
      onUpdate(entry.id, {
        photos: entry.photos.filter((p) => p.id !== photoId),
      });
    },
    [entry.id, entry.photos, onUpdate]
  );

  const handleUpdatePhoto = useCallback(
    (photoId: string, patch: { altText: string }) => {
      onUpdate(entry.id, {
        photos: entry.photos.map((p) => (p.id === photoId ? { ...p, ...patch } : p)),
      });
    },
    [entry.id, entry.photos, onUpdate]
  );

  const handleSetReview = useCallback(
    (reviewContent: string) => {
      onUpdate(entry.id, { reviewContent });
    },
    [entry.id, onUpdate]
  );

  const handleRemove = useCallback(() => {
    onRemove(entry.id);
  }, [entry.id, onRemove]);

  // --- Recommend to friends ---
  const [friendPickerVisible, setFriendPickerVisible] = useState(false);

  const handleRecommendConfirm = useCallback(
    (selected: RecommendFriend[]) => {
      onUpdate(entry.id, { recommendTo: selected });
    },
    [entry.id, onUpdate]
  );

  const handleRemoveFriend = useCallback(
    (friendId: string) => {
      onUpdate(entry.id, {
        recommendTo: entry.recommendTo.filter((f) => f.id !== friendId),
      });
    },
    [entry.id, entry.recommendTo, onUpdate]
  );

  return (
    <View className="border border-border rounded-xl p-4 mb-4">
      {/* Restaurant context */}
      <View className="flex-row items-center mb-2">
        <Ionicons name="location-outline" size={14} className="text-muted-foreground/70" />
        <Text className="text-xs text-muted-foreground/70 ml-1" numberOfLines={1}>
          {businessName}
        </Text>
      </View>

      {/* Entry header */}
      <View className="flex-row items-center justify-between mb-2">
        <Text className="text-sm font-medium text-muted-foreground">
          Item {entryIndex + 1}
        </Text>
        {canRemove && (
          <Pressable
            onPress={handleRemove}
            disabled={isSubmitting}
            hitSlop={8}
            accessibilityLabel={`Remove item ${entryIndex + 1}`}
            className="p-1"
          >
            <Ionicons name="close" size={18} className="text-muted-foreground" />
          </Pressable>
        )}
      </View>

      {/* Menu Item */}
      <MenuItemSearchSection
        businessId={businessId}
        businessName={businessName}
        selectedMenuItem={entry.menuItem}
        onSelect={handleSelectMenuItem}
        onClear={handleClearMenuItem}
      />

      {/* Rating - show after menu item selected */}
      {entry.menuItem && (
        <RatingButtonGroup
          selectedRatingId={entry.ratingId}
          onSelect={handleSetRating}
          disabled={isSubmitting}
        />
      )}

      {/* Divider */}
      {entry.menuItem && <View className="my-2 h-px bg-border/50" />}

      {/* Photos - show after menu item selected */}
      {entry.menuItem && (
        <PhotoSection
          photos={entry.photos}
          onAdd={handleAddPhoto}
          onRemove={handleRemovePhoto}
          onUpdatePhoto={handleUpdatePhoto}
          disabled={isSubmitting}
        />
      )}

      {/* Review - show after menu item selected */}
      {entry.menuItem && (
        <ReviewSection
          value={entry.reviewContent}
          onChange={handleSetReview}
          disabled={isSubmitting}
        />
      )}

      {/* Recommend to friends - show after menu item selected */}
      {entry.menuItem && (
        <View className="mt-2">
          {/* Selected friend chips */}
          {entry.recommendTo.length > 0 && (
            <View className="flex-row flex-wrap mb-1.5" style={{ gap: 6 }}>
              {entry.recommendTo.map((friend) => {
                return (
                  <View
                    key={friend.id}
                    className="flex-row items-center bg-surface-secondary rounded-full pl-1 pr-1.5 py-0.5"
                    style={{ gap: 4 }}
                  >
                    <UserAvatar
                      uri={friend.image}
                      name={friend.name}
                      size={20}
                      decorative
                      className="bg-muted"
                    />
                    <Text className="text-xs text-foreground" numberOfLines={1}>
                      {friend.name}
                    </Text>
                    <Pressable
                      onPress={() => handleRemoveFriend(friend.id)}
                      hitSlop={6}
                      accessibilityLabel={`Remove ${friend.name}`}
                    >
                      <Ionicons name="close-circle" size={14} className="text-muted-foreground" />
                    </Pressable>
                  </View>
                );
              })}
            </View>
          )}

          {/* Recommend pill button */}
          <Pressable
            onPress={() => setFriendPickerVisible(true)}
            disabled={isSubmitting}
            className="flex-row items-center self-start px-3 py-1.5 rounded-full bg-primary/[0.09]"
            style={{ gap: 5 }}
            accessibilityLabel="Recommend to a friend"
          >
            <Ionicons name="paper-plane-outline" size={13} className="text-primary" />
            <Text className="text-sm font-medium text-primary">
              Recommend
            </Text>
          </Pressable>

          <FriendPickerSheet
            visible={friendPickerVisible}
            onClose={() => setFriendPickerVisible(false)}
            onConfirm={handleRecommendConfirm}
            initialSelected={entry.recommendTo}
            menuItemId={entry.menuItem?.id}
          />
        </View>
      )}

      {/* Completeness hint - show after menu item selected */}
      {entry.menuItem && (
        <CompletenessHint
          hasPhotos={entry.photos.length > 0}
          hasReview={entry.reviewContent.trim().length > 0}
          visible={!isSubmitting}
        />
      )}
    </View>
  );
}

/**
 * Memoized so editing one entry (e.g. a keystroke in its review field) doesn't
 * re-render the sibling entry cards. Parent handlers are useCallback-stable and
 * only the edited entry's `entry` reference changes.
 */
export const CheckInEntryCard = memo(CheckInEntryCardComponent);

export default CheckInEntryCard;
